/* eslint-disable @altermaind/you-shall-not-pass */
/* eslint-disable no-nested-ternary */
import { FC, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useStyle, useThemeValue, Svg, Button, Asset } from '@altermaind/web/ui';
import { Claims, FeatureFlag, Roles, Routes } from '@/_shared/constants';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { getEmployeesMeCall } from '@/profile/state/api';
import { getTemplatesCall } from '@/surveys/state/api';
import { ClaimsComponent } from '../Claims/Claims';
import { useMenu } from './useMenu';

type ILink = {
  key: string;
  name: string;
  url?: string;
  icon?: string;
  subItems?: ILink[];
  ff?: FeatureFlag | FeatureFlag[] | null;
  ffMode?: 'every' | 'some';
  claim?: Claims | Claims[] | null;
  hasComingSoon?: boolean;
  roles?: Roles[];
};

type Props = {
  handleMenuToggle?: () => void;
};

export const Menu: FC<Props> = ({ handleMenuToggle }) => {
  const t = useTranslations('menu');
  const { isDesktop } = useBreakingPoints();

  const xxsSpace = useThemeValue('space', 'xxs');
  const [elementSubInHover, setElementSubInHover] = useState<
    { parent: number; subIndex: number } | undefined
  >();

  const { menu } = getTemplatesCall.hook();

  const { hasManagerLeader, hasTeamLeader } = getEmployeesMeCall.hook();

  const links: ILink[] = [
    {
      key: 'homepage',
      name: t('homepage'),
      url: Routes.Homepage,
      icon: 'dashboard',
      subItems: [],
      // TODO: change naming in homepage
      ff: FeatureFlag.DASHBOARD,
      claim: Claims.DASHBOARD_READ,
    },
    {
      key: 'myAssessments',
      name: t('myAssessments'),
      url: '#',
      icon: 'document',
      subItems: [
        ...(menu.length > 0
          ? menu.map(item => ({
              key: `myAssessments-${item.id}`,
              name: item.name,
              url: `${Routes.AssessmentDetail.replace(':templateId', item.id)}`,
              icon: 'document',
              ff: FeatureFlag.ASSESSMENT,
              claim: Claims.PROFILE_READ,
              roles: [Roles.Manager, Roles.Employee],
            }))
          : []),
      ],
      ff: FeatureFlag.PROFILE,
      claim: Claims.PROFILE_READ,
      roles: [Roles.Manager, Roles.Employee],
    },
    {
      key: 'companyStructure',
      name: t('companyStructure'),
      url: '#',
      icon: 'company',
      subItems: [
        {
          key: 'departments',
          name: t('departments'),
          url: Routes.Departments,
          icon: 'team',
          ff: FeatureFlag.DEPARTMENTS,
          claim: Claims.DEPARTMENTS_READ,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
        {
          key: 'people',
          name: t('people'),
          url: Routes.People,
          icon: 'users',
          ff: FeatureFlag.PEOPLE,
          claim: Claims.PEOPLE_READ,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
      ],
      ff: FeatureFlag.ORGANIZATION,
      claim: Claims.ORGANIZATION_READ,
      roles: [Roles.Admin, Roles.HR_Delegate],
    },
    {
      key: 'survey',
      name: t('survey'),
      url: Routes.Surveys,
      icon: 'document',
      subItems: [
        ...(menu.length > 0
          ? menu.map(item => ({
              key: `survey-${item.id}`,
              name: item.name,
              url: `${Routes.SurveyDetail.replace(':surveyId', item.id)}`,
              icon: 'document',
              ff: FeatureFlag.SURVEY,
              claim: Claims.SURVEYS_WRITE,
              roles: [Roles.Admin, Roles.HR_Delegate],
            }))
          : []),
      ],
      ff: FeatureFlag.SURVEY,
      claim: Claims.SURVEYS_WRITE,
      roles: [Roles.Admin, Roles.HR_Delegate],
    },
    {
      key: 'training',
      name: t('training'),
      url: '#',
      icon: 'rocket',
      subItems: [
        {
          key: 'ai-playground',
          name: t('aiPlayground'),
          url: Routes.AiPlayground,
          icon: 'rocket',
          ff: FeatureFlag.TRAINING,
          claim: Claims.TRAINING_READ,
        },
        {
          key: 'training-soft-skills',
          name: t('trainingSoftSkills'),
          url: Routes.TrainingSoftSkills,
          icon: 'rocket',
          ff: FeatureFlag.TRAINING,
          claim: Claims.TRAINING_READ,
        },
        {
          key: 'training-hard-skills',
          name: t('trainingHardSkills'),
          url: Routes.TrainingHardSkills,
          icon: 'rocket',
          ff: FeatureFlag.TRAINING,
          claim: Claims.TRAINING_READ,
        },
      ],
      claim: Claims.TRAINING_READ,
    },
    hasManagerLeader
      ? {
          key: 'department',
          name: t('department'),
          url: Routes.Department,
          icon: 'users',
          hasComingSoon: false,
          subItems: [],
          claim: Claims.DEPARTMENT_READ,
          ff: FeatureFlag.DEPARTMENT,
          roles: [Roles.Manager],
        }
      : null,
    hasTeamLeader
      ? {
          key: 'department',
          name: t('team'),
          url: Routes.Team,
          icon: 'users',
          hasComingSoon: false,
          subItems: [],
          claim: Claims.DEPARTMENT_READ,
          ff: FeatureFlag.DEPARTMENT,
          roles: [Roles.Manager],
        }
      : null,
    {
      key: 'settings',
      name: t('settings'),
      url: Routes.Settings,
      icon: 'aspect',
      ff: [FeatureFlag.ASPECT, FeatureFlag.ORGANIZATION],
      ffMode: 'some',
      subItems: [],
      claim: [Claims.ASPECT_READ, Claims.ORGANIZATION_READ],
      roles: [Roles.Admin, Roles.HR_Delegate],
    } as ILink,
    {
      key: 'reports',
      name: t('reports'),
      url: '#',
      icon: 'docsRar',
      ff: FeatureFlag.REPORTS,
      subItems: [
        {
          key: 'reports-assessments',
          name: t('assessmentsReports'),
          url: Routes.ReportsAssessments,
          icon: 'departments',
          ff: FeatureFlag.REPORTS,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
        {
          key: 'reports-training',
          name: t('trainingReports'),
          url: Routes.ReportsTrainings,
          icon: 'users',
          ff: FeatureFlag.REPORTS,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
      ],
      roles: [Roles.Admin, Roles.HR_Delegate],
    },
  ].filter(menuItem => menuItem !== null);

  const openChatbot = () => {
    const chatbotIcon = document.querySelector(
      '[data-testid="chatbot-icon"]',
    ) as HTMLElement | null;
    if (chatbotIcon) {
      chatbotIcon.click();
    }
  };

  const { isOpen, toggle, hasSubMenu, goTo, isPathMatch } = useMenu();

  const hasAccent = window.accentLight || window.accentDark;

  const hoverStyle = useStyle({
    backgroundColor: hasAccent ? 'accent/background' : 'grey/dark/background',
    borderRadius: 'l',
  });

  const colorDefault = useMemo(() => {
    if (hasAccent) {
      return 'accent';
    }
    return 'primary';
  }, [hasAccent]);

  const { isFeatureEnabled } = useFeatureFlags();
  const isChatBotEnabled = isFeatureEnabled(FeatureFlag.CHATBOT);

  return (
    <>
      {!isDesktop && (
        <Box display='flex' justifyContent='space-between' alignItems='center' mb='s'>
          <Asset domain='images' name='logoDark' height='xl' />
          <Box onClick={handleMenuToggle} textAlign='right'>
            <Svg name='close' size='s' />
          </Box>
        </Box>
      )}
      {links.map((link: ILink, index: number) => {
        // an active sub-item wins over its parent: in that case the parent must not be
        // highlighted (e.g. on /surveys/:id only the sub-item is highlighted, not the parent)
        const hasActiveSubItem = !!link.subItems?.some(sub =>
          isPathMatch(sub.url, window.location.pathname),
        );
        const isLinkActive = isPathMatch(link.url, window.location.pathname) && !hasActiveSubItem;
        return (
          <ClaimsComponent
            key={link.key}
            featureFlag={link.ff || undefined}
            featureFlagCheckMode={link.ffMode || undefined}
            claimKey={link.claim || undefined}
            roles={link.roles || undefined}
            notCheckWriteOrRead
          >
            <Box key={link.key}>
              <Box
                data-testid={`menu-item-${link.name.toLowerCase().toLowerCase().replace(/\s+/g, '-')}`}
                style={{ gap: xxsSpace, cursor: 'pointer' }}
                onClick={() => {
                  if (hasSubMenu(link)) {
                    toggle(index);
                  } else {
                    goTo(link.url, undefined, false);
                    if (handleMenuToggle) handleMenuToggle();
                  }
                }}
              >
                <Box
                  data-testid={`menu-item-${link.name.toLowerCase().replace(/\s+/g, '-')}-header`}
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    ...(isLinkActive ? hoverStyle : {}),
                  }}
                  pl='xs'
                  pr='xxs'
                >
                  <Box
                    display='flex'
                    alignItems='center'
                    style={{
                      gap: xxsSpace,
                    }}
                    width='100%'
                    justifyContent='space-between'
                  >
                    <Box display='flex' alignItems='center' style={{ gap: xxsSpace }}>
                      {link.icon && (
                        <Svg
                          name={link.icon}
                          stroke={isLinkActive ? colorDefault : 'primary/text'}
                          fill={isLinkActive ? colorDefault : 'primary/text'}
                          size='xs'
                        />
                      )}
                      <Typography
                        py='xxs'
                        variant='h4'
                        color={isLinkActive ? colorDefault : 'primary/text'}
                      >
                        {link.name}
                      </Typography>
                    </Box>
                    {link.hasComingSoon && (
                      <Box borderRadius='m' px='xxs' backgroundColor={colorDefault}>
                        <Typography variant='p2/light' fontSize='xs'>
                          {t('comingSoon')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {hasSubMenu(link) && (
                    <Box
                      style={{
                        cursor: 'pointer',
                        transform: isOpen(index, link.key, link.url)
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transformOrigin: 'center',
                        padding: 'none',
                        margin: 'none',
                      }}
                      height='s'
                      display='inline-block'
                      transition='transform .1s ease-in-out'
                    >
                      <Svg
                        fill={isLinkActive ? colorDefault : 'primary/text'}
                        name='down'
                        size='s'
                      />
                    </Box>
                  )}
                </Box>
                {hasSubMenu(link) && (
                  <Box
                    display='grid'
                    gridTemplateRows={isOpen(index, link.key, link.url) ? '1fr' : '0fr'}
                    transition='grid-template-rows .2s ease-in-out'
                    pl='xs'
                  >
                    <Box
                      display='flex'
                      borderLeftWidth={1}
                      borderLeftColor='grey/dark/background'
                      borderLeftStyle='solid'
                      flexDirection='column'
                      style={{ overflow: 'hidden' }}
                    >
                      {link.subItems?.map((subLink, indexSub) => {
                        const isSubLinkActive = isPathMatch(subLink.url, window.location.pathname);

                        const isSubItemInHover =
                          !!elementSubInHover &&
                          elementSubInHover.parent === index &&
                          elementSubInHover.subIndex === indexSub;

                        const conditionHoverOrSelectedSubItem = isSubItemInHover || isSubLinkActive;
                        return (
                          <ClaimsComponent
                            key={subLink.key}
                            featureFlag={subLink.ff || undefined}
                            featureFlagCheckMode={subLink.ffMode || undefined}
                            claimKey={subLink.claim || undefined}
                            roles={subLink.roles || undefined}
                            notCheckWriteOrRead
                          >
                            <Box
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                ...(isSubLinkActive || isSubItemInHover ? hoverStyle : {}),
                              }}
                              onMouseEnter={() =>
                                setElementSubInHover({ parent: index, subIndex: indexSub })
                              }
                              onMouseLeave={() => setElementSubInHover(undefined)}
                              pt='xxs'
                              pr='xxs'
                              pl='xs'
                              display='flex'
                              alignItems='center'
                              pb='xxs'
                              onClick={e => {
                                e.stopPropagation();
                                goTo(subLink.url, index);
                                if (handleMenuToggle) handleMenuToggle();
                              }}
                              justifyContent='space-between'
                              data-testid={`submenu-item-${subLink.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <Box display='flex' alignItems='center' style={{ gap: xxsSpace }}>
                                {subLink.icon && (
                                  <Svg
                                    name={subLink.icon}
                                    stroke={
                                      conditionHoverOrSelectedSubItem
                                        ? colorDefault
                                        : 'grey/light/background'
                                    }
                                    fill={
                                      conditionHoverOrSelectedSubItem
                                        ? colorDefault
                                        : 'grey/light/background'
                                    }
                                    size='xs'
                                  />
                                )}
                                <Typography
                                  ml='xxxxs'
                                  key={subLink.key}
                                  variant='p1/light'
                                  cursor='pointer'
                                  color={
                                    conditionHoverOrSelectedSubItem
                                      ? colorDefault
                                      : 'grey/light/background'
                                  }
                                >
                                  {subLink.name}
                                </Typography>
                              </Box>
                              {subLink.hasComingSoon && (
                                <Box borderRadius='m' px='xxs' backgroundColor={colorDefault}>
                                  <Typography variant='p2/light' fontSize='xs'>
                                    {t('comingSoon')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </ClaimsComponent>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </ClaimsComponent>
        );
      })}
      {isChatBotEnabled && (
        <Button mt='xs' width='100%' variant='primary' onClick={openChatbot}>
          <Svg name='ai' size='s' />
          <Typography as='span' variant='p1' ml='xxs'>
            {t('chatBot')}
          </Typography>
        </Button>
      )}
    </>
  );
};
